import type { StateCreator } from 'zustand';
import type { MessageActionsSlice, WhatsAppChatStore } from '../types';
import { getWahaService } from '../../../services/wahaApiService';
import { applyReaction } from '../../../services/whatsapp/cloudPayloads';

export const createMessageActionsSlice: StateCreator<
  WhatsAppChatStore,
  [],
  [],
  MessageActionsSlice
> = (set, get) => ({
  deleteMessageAction: async (messageId: string) => {
    const { wahaConfig, selectedChatId, messages, provider } = get();
    if (!wahaConfig || !selectedChatId) return;
    if (provider && provider.type !== 'whatsapp') {
      console.warn('[ChatStore] deleteMessage non disponibile sul provider Cloud');
      return;
    }

    // Ottimistica: marca come eliminato nell'UI
    set({
      messages: messages.map((m) =>
        m.id === messageId ? { ...m, isDeleted: true } : m
      ),
    });

    try {
      const service = getWahaService();
      await service.deleteMessage(selectedChatId, messageId);
    } catch (error) {
      // Rollback
      set({
        messages: get().messages.map((m) =>
          m.id === messageId ? { ...m, isDeleted: false } : m
        ),
      });
      console.error('[ChatStore] deleteMessage error:', error);
    }
  },

  editMessageAction: async (messageId: string, newText: string) => {
    const { wahaConfig, selectedChatId, messages, provider } = get();
    if (!wahaConfig || !selectedChatId) return;
    if (provider && provider.type !== 'whatsapp') {
      console.warn('[ChatStore] editMessage non disponibile sul provider Cloud');
      return;
    }

    const original = messages.find((m) => m.id === messageId);
    // Ottimistica
    set({
      messages: messages.map((m) =>
        m.id === messageId ? { ...m, content: newText, isEdited: true } : m
      ),
    });

    try {
      const service = getWahaService();
      await service.editMessage(selectedChatId, messageId, newText);
    } catch (error) {
      // Rollback
      if (original) {
        set({
          messages: get().messages.map((m) =>
            m.id === messageId ? original : m
          ),
        });
      }
      console.error('[ChatStore] editMessage error:', error);
    }
  },

  reactToMessageAction: async (messageId: string, emoji: string) => {
    const { wahaConfig, provider, selectedChatId } = get();
    if (!wahaConfig) return;
    // Numero Ufficiale: reazione via Meta (meta-cloud-proxy la salva anche in
    // whatsapp_message_reactions e avvisa il CRM). Mai una sessione WAHA.
    const cloud = !!provider && provider.type !== 'whatsapp';
    if (cloud && (!provider.sendReaction || !selectedChatId)) return;
    // Subito in chat: la conferma arriva dopo col broadcast. Se l'invio fallisce si torna indietro.
    const before = get().messages.find((m) => m.id === messageId)?.reactions;
    set({
      messages: get().messages.map((m) =>
        m.id === messageId ? { ...m, reactions: applyReaction(m.reactions, emoji) } : m
      ),
    });
    try {
      if (cloud) {
        await provider.sendReaction!(selectedChatId!, messageId, emoji);
      } else if (emoji) {
        await getWahaService().reactToMessage(messageId, emoji);
      } else {
        await getWahaService().removeReaction(messageId);
      }
    } catch (error) {
      set({
        messages: get().messages.map((m) => (m.id === messageId ? { ...m, reactions: before } : m)),
      });
      console.error('[ChatStore] reactToMessage error:', error);
      throw error;
    }
  },

  forwardMessageAction: async (messageId: string, toChatId: string) => {
    const { wahaConfig, provider } = get();
    if (!wahaConfig) return;
    if (provider && provider.type !== 'whatsapp') {
      console.warn('[ChatStore] forwardMessage non disponibile sul provider Cloud');
      return;
    }
    try {
      const service = getWahaService();
      await service.forwardMessage(messageId, toChatId);
    } catch (error) {
      console.error('[ChatStore] forwardMessage error:', error);
      throw error;
    }
  },

  pinMessageAction: async (messageId: string) => {
    const { wahaConfig, selectedChatId, provider } = get();
    if (!wahaConfig || !selectedChatId) return;
    if (provider && provider.type !== 'whatsapp') {
      console.warn('[ChatStore] pinMessage non disponibile sul provider Cloud');
      return;
    }
    try {
      const service = getWahaService();
      await service.pinMessage(selectedChatId, messageId);
    } catch (error) {
      console.error('[ChatStore] pinMessage error:', error);
    }
  },
});
